import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, FileText, Trash2, Download, Link, Loader2, FolderOpen } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientPackagesModal = ({ patient, isOpen, onClose }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [description, setDescription] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && patient?.id) {
      fetchPackages();
    }
  }, [isOpen, patient?.id]);

  const getToken = () => {
    // Try ivory_user first (main app key)
    const ivoryUser = localStorage.getItem('ivory_user');
    if (ivoryUser) {
      try {
        const userData = JSON.parse(ivoryUser);
        return userData.token;
      } catch {
        // fall through
      }
    }
    // Fallback to 'user' key
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        return userData.token;
      } catch {
        return localStorage.getItem('token');
      }
    }
    return localStorage.getItem('token');
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patient.id}/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPackages(data.packages || []);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (15MB max)
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 15MB.');
      return;
    }

    setUploading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description || file.name);

      const response = await fetch(`${API_URL}/api/patients/${patient.id}/packages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Package uploaded successfully');
        setDescription('');
        fetchPackages();
      } else {
        toast.error(data.error || data.detail || 'Failed to upload package');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload package');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLinkAdmissionPDF = async () => {
    setLinking(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patient.id}/packages/link-admission-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Admission PDF linked');
        fetchPackages();
      } else {
        toast.error(data.error || data.detail || 'Failed to link admission PDF');
      }
    } catch (error) {
      console.error('Link error:', error);
      toast.error('Failed to link admission PDF');
    } finally {
      setLinking(false);
    }
  };

  const handleDownload = async (pkg) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patient.id}/packages/${pkg.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success && data.package?.fileData) {
        // Convert base64 to blob and download
        const byteCharacters = atob(data.package.fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.package.contentType });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.package.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (pkg) => {
    if (!confirm(`Delete "${pkg.filename}"?`)) return;

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients/${patient.id}/packages/${pkg.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Package deleted');
        fetchPackages();
      } else {
        // Handle both error and detail fields, normalize array details
        let errorMsg = data.error || data.detail || 'Failed to delete package';
        if (Array.isArray(errorMsg)) {
          errorMsg = errorMsg.map(e => e.msg || e.message || String(e)).join(', ');
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete package');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="patient-packages-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#3498db] to-[#2980b9] p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Application Packages</h2>
                <p className="text-white/80 text-sm">
                  {patient?.firstName} {patient?.lastName}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Upload Section */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#3498db] hover:bg-[#2980b9]"
                  data-testid="upload-package-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload File
                </Button>
                <Button
                  onClick={handleLinkAdmissionPDF}
                  disabled={linking}
                  variant="outline"
                  className="text-green-600 border-green-600 hover:bg-green-50"
                  data-testid="link-admission-pdf-btn"
                >
                  {linking ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Link className="w-4 h-4 mr-2" />
                  )}
                  Link Admission PDF
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Supports PDF, DOC, DOCX, PNG, JPG, TXT (max 15MB)
            </p>
          </div>

          {/* Packages List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#3498db]" />
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No packages uploaded yet</p>
              <p className="text-sm">Upload files or link the admission PDF</p>
            </div>
          ) : (
            <div className="space-y-2">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`package-item-${pkg.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      pkg.isAdmissionPDF ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <FileText className={`w-5 h-5 ${pkg.isAdmissionPDF ? 'text-green-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{pkg.filename}</p>
                      <p className="text-xs text-gray-500">
                        {pkg.description} • {formatFileSize(pkg.fileSize)} • {new Date(pkg.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(pkg)}
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      data-testid={`download-package-${pkg.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(pkg)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      data-testid={`delete-package-${pkg.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default PatientPackagesModal;
